import { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { SubmitHandler } from 'react-hook-form';
import InAppRoutes, { pushProgressive } from 'utils/router';
import { getKit, updateKit } from 'actions/requests';
import { useThunkDispatch } from 'utils/reduxHook';
import { toast } from 'react-toastify';
import { Kits, Templates, PlaybookResource, DiscoverItemType } from 'utils/gloo-types';
import Button from 'components/atoms/Button';
import Card from 'components/atoms/Card';
import KitForm from 'pages/_AdminAuthoringPages/_components/KitForm';
import KitPreviewCard from 'pages/_AdminAuthoringPages/_components/KitPreviewCard';
import Typography from 'components/atoms/Typography';
import KitTemplatesList from 'pages/_AdminAuthoringPages/_components/KitTemplatesList';
import KitResourcesList from 'pages/_AdminAuthoringPages/PlaybookResourcesList';
import { omit } from 'lodash';
import DiscoverItemArchiveToggleButton from 'pages/_AdminAuthoringPages/_components/DiscoverItemArchiveToggleButton';
import classNames from 'classnames';

function AdminKitEdit() {
  const dispatch = useThunkDispatch();
  const kitId = useParams<{ kitId: string }>().kitId;
  const [kit, setKit] = useState<Kits>();
  const [selectedTemplates, setSelectedTemplates] = useState<Templates[]>();
  const [resources, setResources] = useState<PlaybookResource[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const loadKit = async () => {
      try {
        setIsLoading(true);
        const existingKit = await dispatch(getKit(kitId));
        setKit(existingKit);
        setSelectedTemplates(existingKit.templates);
        setResources(existingKit.resources);
      } finally {
        setIsLoading(false);
      }
    };
    loadKit();
  }, []);

  const handleKitSave: SubmitHandler<Partial<Kits>> = async (data) => {
    const selectedTemplateIds = selectedTemplates.map((template) => template.id);
    const updateData = {
      ...omit(data, ['featuredPriority']),
      templateIds: selectedTemplateIds,
      resources,
    };

    const updatedKit = await dispatch(updateKit(kit.id, updateData));
    if (!updatedKit.error) toast.success('Your changes have been saved.');
  };

  return (
    <article className="container-1">
      <Card className="flex items-center justify-between p-4 m-2">
        <Button
          dataDurableId="KitBackButton"
          type="secondary"
          icon="arrow-left"
          onClick={() => {
            if (!location.key) {
              pushProgressive(InAppRoutes.AdminAuthoringHome);
            } else {
              history.goBack();
            }
          }}
          text="Back"
          className={classNames('mr-4')}
        />
        <Typography className="text-thryveGreenDark flex-grow" variant="h1">
          Edit Kit
        </Typography>
        <div className="flex flex-row gap-4">
          {kit && (
            <DiscoverItemArchiveToggleButton
              archivedAt={kit.archivedAt}
              descriptor={{
                type: DiscoverItemType.KIT,
                id: kit.id,
              }}
              onArchivedAtChange={(archivedAt) => {
                setKit({ ...kit, archivedAt });
              }}
            />
          )}
          <Button
            icon="save"
            iconPosition="left"
            buttonType="submit"
            form="kitEditForm"
            dataDurableId="adminKitEditSaveButton"
            type="primary"
            text="SAVE"
            disabled={!!kit && !!kit.archivedAt}
          />
        </div>
      </Card>
      <section className="grid grid-cols-4 grid-rows-6">
        <Card className="p-4 m-2 col-span-3 row-span-6">
          {!isLoading && (
            <KitForm formId="kitEditForm" kit={kit} handleOnChange={setKit} handleKitSave={handleKitSave} />
          )}
        </Card>
        <div className="col-span-1 row-span-1 p-4">
          <Typography className="text-thryveGray my-4" variant="h3">
            PREVIEW:
          </Typography>
          {kit && <KitPreviewCard kitPreview={kit} />}
        </div>
        <Card className="col-span-1 row-span-5">
          <Typography className="text-thryveGray my-4" variant="h3">
            TEMPLATES:
          </Typography>
          {selectedTemplates && <KitTemplatesList handleSelected={setSelectedTemplates} selected={selectedTemplates} />}
          <Typography className="text-thryveGray my-4" variant="h3">
            RESOURCES:
          </Typography>
          {kit && <KitResourcesList handleSelected={setResources} resources={resources} />}
        </Card>
      </section>
    </article>
  );
}

export default AdminKitEdit;
